import React, { useMemo } from "react";
import { Image } from "lucide-react";

// ===== تعریف نوع‌ها =====
export interface OrderImage {
    created_at: string;
    created_at_jalali: string;
    id: number;
    category: string;
    url: string;
    file_name: string;
    file_size: number;
}

export interface DriverTask {
    id: number;
    created_at: string;
    created_at_jalali: string;
    updated_at: string;
    updated_at_jalali: string;
    baker_name: string;
    baker_code: number;
    baker_phone: string;
    driver_discharge_count: number | null;
    driver_available_count: number | null;
    driver_location_lat: number | null;
    driver_location_lng: number | null;
    images: OrderImage[];
    discharge_date?: string | null;
    discharge_date_jalali?: string | null;
    driver_name?: string | null;
    driver_phone?: string | null;
    city?: string | null;
    province?: string | null;
    voucher_date?: string | null;
    voucher_date_jalali?: string | null;
    /** آدرس نانوا، خوانده‌شده از پروفایل خبازی توسط Backend */
    baker_address?: string | null;
}

// ===== Props =====
interface DriverTaskColumnsProps {
    userRole: number;
    openSubmitModal: (task: DriverTask) => void;
    openGallery: (images: OrderImage[]) => void;
    openDetailModal: (task: DriverTask) => void;
    openDELETEModal: (task: DriverTask) => void;
    renderSortableHeader: (label: string, accessor: string) => React.ReactNode; // اضافه شد
}

const DriverTaskColumns = ({
    userRole,
    openSubmitModal,
    openGallery,
    openDetailModal,
    openDELETEModal,
    renderSortableHeader,
}: DriverTaskColumnsProps) => {
    return useMemo(() => {
        return [
            // 1. ستون تاریخ ثبت با قابلیت سورت
            {
                header: () => renderSortableHeader("تاریخ ثبت", "discharge_date_jalali"),
                accessorKey: "discharge_date_jalali",
                enableColumnFilter: false,
                cell: (cell: any) => {
                    const row = cell.row.original as DriverTask;
                    return row.discharge_date_jalali || "-";
                },
            },
            // 2. عملیات (فقط برای نقش‌های غیر از ۵ و ۶)
            ...(userRole !== 5 && userRole !== 6
                ? [
                    {
                        header: "عملیات",
                        enableColumnFilter: false,
                        cell: (cell: any) => {
                            const task = cell.row.original as DriverTask;
                            return (
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => openSubmitModal(task)}
                                        className="p-2 text-green-700 border border-green-300 rounded-lg bg-green-50 hover:bg-green-100 hover:shadow-md transition-all duration-200"
                                        title="ثبت تخلیه"
                                    >
                                        ثبت راننده
                                    </button>
                                </div>
                            );
                        },
                    },
                ]
                : []),
            // 3. شهرستان
            {
                header: "شهرستان",
                accessorKey: "city",
                enableColumnFilter: false,
                cell: (cell: any) => cell.getValue() || "نامشخص",
            },
            // 4. نام نانوا
            {
                header: "نام نانوا",
                accessorKey: "baker_name",
                enableColumnFilter: false,
                cell: (cell: any) => cell.getValue() || "-",
            },
            // 5. شماره تماس نانوا
            {
                header: "شماره تماس نانوا",
                accessorKey: "baker_phone",
                enableColumnFilter: false,
                cell: (cell: any) => cell.getValue() || "-",
            },
            // آدرس نانوا: مرجع آدرس، فیلد baker_address پاسخ همین endpoint است
            // (از پروفایل خبازی خوانده می‌شود و نباید سمت Frontend ساخته شود)
            {
                header: "آدرس نانوا",
                accessorKey: "baker_address",
                enableColumnFilter: false,
                cell: (cell: any) => {
                    const address = cell.getValue();
                    if (!address) return <span className="text-gray-400">ثبت نشده</span>;
                    return (
                        <span className="block max-w-[220px] truncate" title={address}>
                            {address}
                        </span>
                    );
                },
            },
            // 6. تعداد کیسه موجود
            {
                header: "تعداد کیسه موجود",
                accessorKey: "driver_available_count",
                enableColumnFilter: false,
                cell: (cell: any) => (cell.getValue() !== null ? cell.getValue() : "ثبت نشده"),
            },
            // 7. تعداد کیسه تخلیه شده
            {
                header: "تعداد کیسه تخلیه شده",
                accessorKey: "driver_discharge_count",
                enableColumnFilter: false,
                cell: (cell: any) => (cell.getValue() !== null ? cell.getValue() : "ثبت نشده"),
            },
            // 8. مستندات تصویر
            {
                header: "مستندات تصویر",
                accessorKey: "images",
                enableColumnFilter: false,
                cell: (cell: any) => {
                    const images = cell.getValue() as OrderImage[] | undefined;
                    return images && images.length > 0 ? (
                        <button
                            onClick={() => openGallery(images)}
                            className="flex items-center gap-1 text-blue-600 hover:underline"
                        >
                            <Image size={14} /> {images.length} عکس
                        </button>
                    ) : (
                        <span className="text-gray-400">بدون تصویر</span>
                    );
                },
            },
            // 9. نام راننده
            {
                header: "نام راننده",
                accessorKey: "driver_name",
                cell: (cell: any) => cell.getValue() || "ثبت نشده",
            },
            // 10. شماره همراه راننده
            {
                header: "شماره همراه راننده",
                accessorKey: "driver_phone",
                cell: (cell: any) => cell.getValue() || "ثبت نشده",
            },
            // 11. تاریخ حواله
            {
                header: "تاریخ حواله",
                accessorKey: "voucher_date_jalali",
                cell: (cell: any) => cell.getValue() || "ثبت نشده",
            },
            // 12. حذف (فقط برای نقش‌های ۰ و ۱)
            ...(Number(userRole) === 0 || Number(userRole) === 1
                ? [
                    {
                        header: "حذف",
                        enableColumnFilter: false,
                        cell: (cell: any) => {
                            const task = cell.row.original as DriverTask;
                            return (
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => openDELETEModal(task)}
                                        className="p-2 text-red-700 border border-green-300 rounded-lg bg-green-50 hover:bg-green-100 hover:shadow-md transition-all duration-200"
                                        title="حذف"
                                    >
                                        حذف
                                    </button>
                                </div>
                            );
                        },
                    },
                ]
                : []),
        ];
    }, [
        userRole,
        openSubmitModal,
        openGallery,
        openDetailModal,
        openDELETEModal,
        renderSortableHeader,
    ]);
};

export default DriverTaskColumns;